import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface CachedSubscription {
  token: string
  type: 'free' | 'starter' | 'professional' | 'enterprise' | 'protrial'
  verifiedAt: Date
  expiresAt: Date
  offlineAllowed: boolean
}

interface OfflineState {
  lastSubscriptionCheck: Date | null
  subscriptionCache: CachedSubscription | null
  appVersion: string | null
}

interface OfflineActions {
  updateSubscriptionCache: (subscription: CachedSubscription) => void
  clearSubscriptionCache: () => void
  isSubscriptionCacheValid: () => boolean
  setAppVersion: (version: string) => void
}

export const useOfflineStore = create<OfflineState & OfflineActions>()(
  persist(
    (set, get) => ({
      // State
      lastSubscriptionCheck: null,
      subscriptionCache: null,
      appVersion: null,

      // Actions
      updateSubscriptionCache: (subscription: CachedSubscription): void => {
        const now = new Date()

        set({
          subscriptionCache: subscription,
          lastSubscriptionCheck: now
        })
      },

      clearSubscriptionCache: (): void => {
        set({
          subscriptionCache: null,
          lastSubscriptionCheck: null
        })
      },

      isSubscriptionCacheValid: (): boolean => {
        const { subscriptionCache, lastSubscriptionCheck } = get()
        if (!subscriptionCache || !lastSubscriptionCheck) return false

        const now = new Date()
        const cacheAge = now.getTime() - new Date(lastSubscriptionCheck).getTime()
        const maxCacheAge = 12 * 60 * 60 * 1000 // 12 hours in milliseconds

        return cacheAge < maxCacheAge
      },

      setAppVersion: (version: string): void => {
        set({ appVersion: version })
      }
    }),
    {
      name: 'offline-mode-storage',
      partialize: (state) => ({
        subscriptionCache: state.subscriptionCache,
        lastSubscriptionCheck: state.lastSubscriptionCheck,
        appVersion: state.appVersion
      })
    }
  )
)
